import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { locationGroupApi } from '@/api/account-management/locationGroupApi';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import { type Location } from '@/models/account-management/Location';
import { type LocationGroup } from '@/models/account-management/LocationGroup';
import {
    type PlaylistConfiguration as Config, PlaylistConfigurationScope, DEFAULT_SLIDESHOW_INTERVAL_SECONDS,
} from '@/models/media-network/PlaylistConfiguration';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { useToast } from '../common/toast/ToastProvider';
import { useAssignedLocations } from '../../permissions/useLocationScope';

const PlaylistConfigurationPage = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canUpdate = perms.canUpdate(Perm.mediaNetworkAdBanner);
    const canDelete = perms.canDelete(Perm.mediaNetworkAdBanner);

    const brandId = app.brandId ?? undefined;

    const [scopeType, setScopeType] = useState<PlaylistConfigurationScope>(PlaylistConfigurationScope.Brand);
    const [scopeId, setScopeId] = useState('');
    const [seconds, setSeconds] = useState(String(DEFAULT_SLIDESHOW_INTERVAL_SECONDS));

    const { data: configs } = useQuery<Config[]>({
        queryKey: mediaNetworkKeys.configurations(brandId ?? ''),
        enabled: !!brandId,
        queryFn: () => mediaNetworkApi.listConfigurations(brandId!),
        staleTime: 0,
    });
    // Only the locations this user is assigned to (TTD-160).
    const { locations } = useAssignedLocations(brandId);
    const { data: groups } = useQuery<LocationGroup[]>({
        queryKey: ['location-groups', brandId ?? ''],
        enabled: !!brandId,
        queryFn: () => locationGroupApi.list(brandId!),
        staleTime: Infinity,
    });

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: () => mediaNetworkApi.saveConfiguration({
            brandId: brandId!,
            scopeType,
            // Brand scope always targets the brand itself, so the picker is skipped for it.
            scopeId: scopeType === PlaylistConfigurationScope.Brand ? brandId! : scopeId,
            slideshowIntervalSeconds: Number(seconds),
        }),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.playlistConfigurationSaved });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.configurations(brandId ?? '') });
            // A save at ANY scope can change what a location resolves to, and the preview reads that number.
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.effectiveConfigurations() });
            setScopeId('');
        },
        onError: () => toast({ severity: 'error', message: strings.playlistConfigurationSaveError }),
    });

    const { mutateAsync: remove } = useMutation({
        mutationFn: (id: string) => mediaNetworkApi.deleteConfiguration(id, brandId!),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.playlistConfigurationDeleted });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.configurations(brandId ?? '') });
            // A save at ANY scope can change what a location resolves to, and the preview reads that number.
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.effectiveConfigurations() });
        },
        onError: () => toast({ severity: 'error', message: strings.playlistConfigurationDeleteError }),
    });

    const scopeName = (c: Config): string => {
        if (c.scopeType === PlaylistConfigurationScope.Brand) return strings.playlistScopeEntireBrand;
        if (c.scopeType === PlaylistConfigurationScope.LocationGroup) {
            return groups?.find(g => g.id === c.scopeId)?.name ?? strings.playlistUnknownTarget;
        }
        return locations?.find(l => l.id === c.scopeId)?.name ?? strings.playlistUnknownTarget;
    };

    if (!brandId) {
        return <Typography variant="body2" color="text.secondary">{strings.playlistSelectBrandTopBar}</Typography>;
    }

    const needsTarget = scopeType !== PlaylistConfigurationScope.Brand;
    const canSave = Number(seconds) >= 1 && (!needsTarget || !!scopeId);

    // Most specific wins, so show them in the order they'd actually be consulted.
    const sorted = [...(configs ?? [])].sort((a, b) => b.scopeType - a.scopeType);

    // No padding of its own: this renders inside the tab panel, which already pads.
    return (
        <Box sx={{ height: '100%', overflow: 'auto' }}>
            <Stack spacing={2} sx={{ maxWidth: 900 }}>
                <Typography variant="h6">{strings.playlistSlideshowIntervalTitle}</Typography>

                {/* The inheritance chain is the whole feature here; spelling it out beats making someone
                    infer it from a list of rows. */}
                <Alert severity="info" variant="outlined">
                    {strings.playlistConfigurationHelp.replace('{{default}}', String(DEFAULT_SLIDESHOW_INTERVAL_SECONDS))}
                </Alert>

                {canUpdate && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel id="scope-type-label">{strings.playlistAppliesTo}</InputLabel>
                                <Select
                                    labelId="scope-type-label"
                                    label={strings.playlistAppliesTo}
                                    value={scopeType}
                                    onChange={(e) => { setScopeType(Number(e.target.value) as PlaylistConfigurationScope); setScopeId(''); }}
                                >
                                    <MenuItem value={PlaylistConfigurationScope.Brand}>{strings.playlistScopeEntireBrand}</MenuItem>
                                    <MenuItem value={PlaylistConfigurationScope.LocationGroup}>{strings.locationGroup}</MenuItem>
                                    <MenuItem value={PlaylistConfigurationScope.Location}>{strings.location}</MenuItem>
                                </Select>
                            </FormControl>

                            {needsTarget && (
                                <FormControl sx={{ minWidth: 220 }}>
                                    <InputLabel id="scope-id-label">
                                        {scopeType === PlaylistConfigurationScope.LocationGroup ? strings.locationGroup : strings.location}
                                    </InputLabel>
                                    <Select
                                        labelId="scope-id-label"
                                        label={scopeType === PlaylistConfigurationScope.LocationGroup ? strings.locationGroup : strings.location}
                                        value={scopeId}
                                        onChange={(e) => setScopeId(e.target.value)}
                                    >
                                        {scopeType === PlaylistConfigurationScope.LocationGroup
                                            ? (groups ?? []).filter(g => !!g.id).map(g => <MenuItem key={g.id} value={g.id!}>{g.name}</MenuItem>)
                                            : (locations ?? []).map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}

                            <TextField
                                label={strings.playlistSlideshowInterval}
                                type="number"
                                value={seconds}
                                onChange={(e) => setSeconds(e.target.value)}
                                helperText={strings.playlistSlideshowIntervalHelp}
                                slotProps={{ htmlInput: { min: 1 } }}
                                sx={{ minWidth: 200 }}
                            />

                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                loading={isPending}
                                disabled={!canSave || isPending}
                                onClick={() => save()}
                                size="input"
                            >
                                {strings.save}
                            </Button>
                        </Stack>
                    </Paper>
                )}

                <Stack spacing={1}>
                    {sorted.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {strings.playlistNoConfigurations.replace('{{default}}', String(DEFAULT_SLIDESHOW_INTERVAL_SECONDS))}
                        </Typography>
                    )}

                    {sorted.map((c) => (
                        <Paper key={c.id} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Chip
                                    size="small"
                                    label={
                                        c.scopeType === PlaylistConfigurationScope.Location ? strings.location
                                        : c.scopeType === PlaylistConfigurationScope.LocationGroup ? strings.locationGroup
                                        : strings.brand
                                    }
                                    color={c.scopeType === PlaylistConfigurationScope.Location ? 'primary' : 'default'}
                                />
                                <Typography variant="body2" sx={{ flex: 1 }}>{scopeName(c)}</Typography>
                                <Typography variant="body2">
                                    {strings.playlistSecondsPerImage.replace('{{seconds}}', String(c.slideshowIntervalSeconds))}
                                </Typography>
                                {canDelete && (
                                    <IconButton size="small" color="error" onClick={() => remove(c.id)} aria-label="delete configuration">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            </Stack>
        </Box>
    );
};

export default PlaylistConfigurationPage;
